import { css } from "@emotion/react";
import {
  EuiButton,
  EuiFieldText,
  EuiFlexGroup,
  EuiFlexItem,
  EuiForm,
  EuiFormRow,
  EuiPanel,
  EuiSpacer,
  EuiText,
  EuiTextArea,
} from "@inscopix/ideas-eui";
import axios from "axios";
import { getEnvVar } from "ideas.env";
import { ModalDeleteTool } from "components/ModalDeleteTool/ModalDeleteTool";
import { useToolDataContext } from "pages/tool/ToolDataProvider";
import { useState } from "react";
import { addUtilityToastFailure } from "utils/addUtilityToastFailure";
import { addUtilityToastSuccess } from "utils/addUtilityToastSuccess";
import { getRequestHeaders } from "utils/getRequestHeaders";
import { isNonNullish } from "utils/isNonNullish";

/**
 * Updates a tool.
 * @param options The options for the tool.
 */
const updateTool = async (options: {
  name: string;
  id: number;
  description: string;
  docs_url: string | null;
  code_url: string | null;
}) => {
  const url = getEnvVar("URL_TRS_TOOL")(options.id);
  const headers = await getRequestHeaders();
  const body = {
    name: options.name,
    help_text: options.description,
    docs_link: options.docs_url,
    code_link: options.code_url,
  };
  await axios.patch(url, body, { headers });
};

export function ToolTabSettings() {
  const { tool, invalidateToolQuery } = useToolDataContext();
  const [isLoading, setIsLoading] = useState(false);
  const [name, setName] = useState(tool.name);
  const [description, setDescription] = useState(
    isNonNullish(tool.help_text) ? tool.help_text : "",
  );
  const [docsLink, setDocsLink] = useState(
    isNonNullish(tool.docs_link) ? tool.docs_link : "",
  );
  const urlWithTldRegex = /^https?:\/\/[^/\s?#]+\.[A-Za-z]{2,63}(?:[/?#]|$)/;
  const docsLinkIsValid = docsLink === "" || urlWithTldRegex.test(docsLink);
  const [codeLink, setCodeLink] = useState(
    isNonNullish(tool.code_link) ? tool.code_link : "",
  );
  const codeLinkIsValid = codeLink === "" || urlWithTldRegex.test(codeLink);

  // on submit, update tool
  const onSubmit = async () => {
    try {
      setIsLoading(true);
      await updateTool({
        name,
        id: tool.id,
        description,
        docs_url: docsLink !== "" ? docsLink : null,
        code_url: codeLink !== "" ? codeLink : null,
      });
      // invalidate tool details query
      await invalidateToolQuery();
      setIsLoading(false);
      addUtilityToastSuccess("Tool settings saved");
    } catch (error) {
      setIsLoading(false);
      addUtilityToastFailure("Updated tool settings failed to save");
    }
  };

  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  return (
    <EuiFlexGroup>
      <EuiFlexItem
        grow={false}
        style={
          // 400px max form width + 24 px padding each side
          { width: "488px" }
        }
      >
        <EuiPanel hasShadow={false} hasBorder={true}>
          <EuiText size="s">
            <h3>Settings</h3>
          </EuiText>
          <EuiSpacer size="s" />
          <EuiForm>
            <EuiFormRow label="Name*">
              <EuiFieldText
                placeholder="Tool Name"
                name="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                icon="copyClipboard"
                aria-label="Tool Name"
                maxLength={255}
              />
            </EuiFormRow>
            <EuiFormRow label="Key">
              <EuiFieldText name="key" value={tool.key} disabled={true} />
            </EuiFormRow>
            <EuiFormRow label="Description">
              <EuiTextArea
                name="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                compressed={true}
              />
            </EuiFormRow>
            <EuiFormRow
              label="Documentation Link"
              helpText="Add an optional link to documentation for building or using this tool"
              isInvalid={!docsLinkIsValid}
              error={
                !docsLinkIsValid
                  ? [
                      "Documentation link must be a valid URL starting with http:// or https://",
                    ]
                  : undefined
              }
            >
              <EuiFieldText
                name="docs_link"
                value={docsLink}
                onChange={(e) => setDocsLink(e.target.value)}
              />
            </EuiFormRow>
            <EuiFormRow
              label="Code Link"
              helpText="Add an optional link to a code repository or download location for this tool"
              isInvalid={!codeLinkIsValid}
              error={
                !codeLinkIsValid
                  ? [
                      "Code link must be a valid URL starting with http:// or https://",
                    ]
                  : undefined
              }
            >
              <EuiFieldText
                name="code_link"
                value={codeLink}
                onChange={(e) => setCodeLink(e.target.value)}
              />
            </EuiFormRow>
            <EuiSpacer size="xl" />
            <EuiButton
              fill
              onClick={() => void onSubmit()}
              isLoading={isLoading}
            >
              Save
            </EuiButton>
          </EuiForm>
        </EuiPanel>
      </EuiFlexItem>
      <EuiFlexItem grow={false}>
        <EuiPanel hasShadow={false} hasBorder={true} style={{ flexGrow: 0 }}>
          <EuiPanel
            hasShadow={false}
            hasBorder={true}
            css={css`
              flex-grow: 0;
            `}
          >
            <EuiText size="s">
              <h3>Actions</h3>
            </EuiText>
            <EuiSpacer size="s" />
            <EuiButton
              iconType={"trash"}
              color={"danger"}
              onClick={() => setIsDeleteModalVisible(true)}
            >
              Delete
            </EuiButton>
          </EuiPanel>
        </EuiPanel>
      </EuiFlexItem>
      {isDeleteModalVisible && (
        <ModalDeleteTool
          toolId={tool.id}
          onClose={() => setIsDeleteModalVisible(false)}
        />
      )}
    </EuiFlexGroup>
  );
}
